import React from "react";
import { Link } from "react-router-dom";
import images from "../utils/imageLoader";
import Seo from "../components/Seo";
import "../style/style-pages/services-page.css";

const serviceCards = [
    {
        title: "Brand Identity",
        subtitle: "Crafting Memorable Identities that Stand Out",
        outcomes: [
            { title: "Brand foundation", detail: "logo, palette, typography, and usage guidance that make every customer touchpoint recognizable and trustworthy." },
            { title: "Launch-ready brand assets", detail: "social, marketing, and presentation templates that help teams move faster without losing consistency." },
        ],
        image: "Services__card1.png",
        icon: "Services__card-icon2.svg",
        path: "/services/brand-identity",
    },
    {
        title: "UI/UX Design",
        subtitle: "Designing Seamless User Experiences",
        outcomes: [
            { title: "User flows and interface designs", detail: "reduce friction, so customers can complete important tasks with confidence." },
            { title: "Interactive prototype and handoff", detail: "align stakeholders before development, reducing rework and delivery risk." },
        ],
        image: "Services__card2.png",
        icon: "Services__card-icon2.svg",
        path: "/services/ui-ux-design",
    },
    {
        title: "Development",
        subtitle: "Building Cutting-Edge Digital Solutions",
        outcomes: [
            { title: "Responsive website or application", detail: "works across devices, helping customers access the service wherever they are." },
            { title: "Production-ready implementation", detail: "creates a clear technical foundation, making future updates safer and more cost-effective." },
        ],
        image: "Services__card3.png",
        icon: "Services__card-icon3.svg",
        path: "/services/web-app-development",
    },
    {
        title: "Animation",
        subtitle: "Bringing Your Vision to Life with Motion",
        outcomes: [
            { title: "Motion guidelines and key interactions", detail: "guide attention, helping users understand what happens next." },
            { title: "Branded storytelling assets", detail: "explain complex ideas clearly, making campaigns and launches more memorable." },
        ],
        image: "Services__card4.png",
        icon: "Services__card-icon4.svg",
        path: "/services/animation",
    },
    {
        title: "UX Strategy",
        subtitle: "Strategizing User-Centric Designs for Success",
        outcomes: [
            { title: "Research, personas, and journey maps", detail: "connect business priorities to real user needs." },
            { title: "Prioritized experience roadmap", detail: "helps teams invest in the highest-value improvements first." },
        ],
        image: "Services__card5.png",
        icon: "Services__card-icon5.svg",
        path: "/services/ux-strategy",
    },
    {
        title: "Practical & Governed AI",
        subtitle: "Specialist AI Workflows with Human Accountability",
        outcomes: [
            { title: "AI opportunity and scope map", detail: "identifies practical support opportunities, data boundaries, owners, and review points before automation begins." },
            { title: "Human-approved workflow prototype", detail: "accelerates synthesis and repetitive work while keeping recommendations, decisions, and external actions traceable." },
        ],
        image: "Services__card6.png",
        icon: "Services__card-icon6.svg",
        path: "/services/ai-business",
    },
];

const Services = () => {
    return (
        <main className="services-page">
            <Seo
                canonicalPath="/services"
                description="Explore WiseCursor services across brand identity, UX and UI design, web development, animation, UX strategy, and practical, governed AI workflows."
                title="Services"
            />
            <section className="services-page__hero">
                <div className="services-page__hero-inner container">
                    <div className="services-page__hero-copy">
                        <span className="services-page__eyebrow">Our Services</span>
                        <h1>
                            Design, build, and automate digital products that move your business forward.
                        </h1>
                        <p className="services-page__hero-summary">
                            WiseCursor combines UX strategy, brand systems, product design, development, and practical, human-approved AI workstreams into one coordinated delivery process.
                        </p>
                        <Link className="services-page__cta" to="/#contact">
                            Start a project
                        </Link>
                    </div>
                    <div className="services-page__hero-media" aria-hidden="true">
                        <img src={images["Services__card5.png"]} alt="" />
                    </div>
                </div>
            </section>

            <section className="services-page__grid-section">
                <div className="container">
                    <div className="services-page__section-heading">
                        <p>Explore the capabilities WiseCursor uses to shape digital products from strategy through launch.</p>
                        <h2>Services built for practical product growth</h2>
                    </div>
                    <div className="services-page__grid">
                        {serviceCards.map((service) => (
                            <Link className="services-page__card" key={service.title} to={service.path} aria-label={`Explore ${service.title}`}>
                                <img className="services-page__card-image" src={images[service.image]} alt="" />
                                <div className="services-page__card-content">
                                    <div className="services-page__card-title">
                                        <img src={images[service.icon]} alt="" />
                                        <h3>{service.title}</h3>
                                    </div>
                                    <p className="services-page__card-subtitle">{service.subtitle}</p>
                                    <ul className="services-page__card-outcomes">
                                        {service.outcomes.map((outcome) => (
                                            <li key={outcome.title}><strong>{outcome.title}:</strong> {outcome.detail}</li>
                                        ))}
                                    </ul>
                                    <span className="services-page__card-cta">Explore {service.title} <span aria-hidden="true">→</span></span>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            <section className="services-page__process">
                <div className="container">
                    <div>
                        <p className="services-page__kicker">How we work</p>
                        <h2>Clear strategy, focused execution, and consistent collaboration.</h2>
                    </div>
                    <div className="services-page__steps">
                        <article>
                            <span>01</span>
                            <h3>Discover</h3>
                            <p>Understand the business objective, audience, constraints, and product opportunity.</p>
                        </article>
                        <article>
                            <span>02</span>
                            <h3>Design</h3>
                            <p>Shape the experience through structure, visuals, content, and interaction patterns.</p>
                        </article>
                        <article>
                            <span>03</span>
                            <h3>Deliver</h3>
                            <p>Prepare the solution for real users with implementation-ready assets and practical next steps.</p>
                        </article>
                    </div>
                </div>
            </section>
        </main>
    );
};

export default Services;
